import React, { ReactNode } from 'react'
import { t } from 'i18next'
import {
    FlagIcon,
    RectIcon,
    TrophyIcon,
    SacIcon,
    PaymentAllIcon,
    ShadedHatIcon
} from 'assets'
import { InteractionOutcomes } from 'types'
import { DesiredOutcomes } from 'storeTypes'
import { colors } from 'styles/colors'

interface PaymentSummary {
    percentage?: number
    allEnrollment?: number
}

interface RecommendedAction {
    icon: ReactNode
    label: string
    className: string
    background?: string
    fontColor?: string
}

interface GetRecommendedActionProps {
    outcome?: string
    desiredOutcome?: string
    duplicate?: boolean
    paymentSummary?: PaymentSummary
    shouldShowColor: boolean
    isVisitedClient: boolean
}

const wrapLabelClass = (baseClass: string, shouldShowColor: boolean): string =>
    `${baseClass}${shouldShowColor ? '' : ' target-list--not-visited'}`

const getDuplicateLabel = (): RecommendedAction => ({
    icon: <FlagIcon color={colors.darkRed} width="22" height="22" />,
    label: t('status.duplicate'),
    className: wrapLabelClass('target-list--duplicate', true),
    background: colors.lightGrey,
    fontColor: colors.darkRed
})

const getNotInterestedLabel = (): RecommendedAction => ({
    icon: <RectIcon height="7" width="21" color={colors.grey} />,
    label: t('targetList.outcome.not_interested'),
    className: wrapLabelClass('target-list--not-interested', true),
    background: colors.lighterRed,
    fontColor: colors.darkGrey
})

const getFinisherLabel = (shouldShowColor: boolean): RecommendedAction => ({
    icon: (
        <TrophyIcon
            color={shouldShowColor ? colors.purple : colors.grey}
            height="22"
            width="22"
        />
    ),
    label: '100% ' + t('targetList.outcome.payment'),
    className: wrapLabelClass('target-list--finisher', shouldShowColor),
    background: colors.darkPurple
})

const getUnvisitedLabel = (
    desiredOutcome: string | undefined,
    allEnrollment: number,
    percentage: number,
    shouldShowColor: boolean
): RecommendedAction => {
    if (
        (desiredOutcome === DesiredOutcomes.PAYMENT_2ND ||
            desiredOutcome === DesiredOutcomes.PAYMENT_3RD ||
            desiredOutcome === DesiredOutcomes.ENROLLED) &&
        allEnrollment > 0 &&
        percentage < 100
    ) {
        return {
            icon: (
                <PaymentAllIcon
                    color={shouldShowColor ? colors.primary : colors.grey}
                    height="22"
                    width="22"
                />
            ),
            label: t('targetList.outcome.payment'),
            className: wrapLabelClass(
                'target-list--all-payment',
                shouldShowColor
            )
        }
    }

    if (desiredOutcome === DesiredOutcomes.PAYMENT_FINISHER) {
        return getFinisherLabel(shouldShowColor)
    }

    return {
        icon: (
            <SacIcon
                color={shouldShowColor ? colors.primary : colors.grey}
                height="22"
                width="22"
            />
        ),
        label: t('targetList.outcome.package'),
        className: wrapLabelClass('target-list--enrolled', shouldShowColor)
    }
}

const getVisitedLabel = (
    outcome: string | undefined,
    allEnrollment: number,
    shouldShowColor: boolean
): RecommendedAction => {
    switch (outcome) {
        case InteractionOutcomes.FOLLOW_UP:
            return {
                icon: <ShadedHatIcon />,
                label: t('targetList.outcome.follow_up'),
                className: wrapLabelClass(
                    'target-list--follow-up',
                    shouldShowColor
                ),
                background: colors.lightYellow
            }

        case InteractionOutcomes.CARD_PAYMENT:
        case InteractionOutcomes.MOBILE_MONEY_PAYMENT:
            if (allEnrollment === 0) {
                return {
                    icon: (
                        <SacIcon
                            height="22"
                            width="22"
                            color={
                                shouldShowColor ? colors.primary : colors.grey
                            }
                        />
                    ),
                    label: t('targetList.outcome.package'),
                    className: wrapLabelClass(
                        'target-list--enrolled',
                        shouldShowColor
                    ),
                    background: colors.InteractionsGreen
                }
            }
            return {
                icon: (
                    <PaymentAllIcon
                        color={shouldShowColor ? colors.primary : colors.grey}
                    />
                ),
                label: t('targetList.outcome.payment'),
                className: wrapLabelClass(
                    'target-list--all-payment',
                    shouldShowColor
                ),
                background: colors.InteractionsGreen
            }

        default:
            return {
                icon: (
                    <SacIcon
                        height="22"
                        width="22"
                        color={shouldShowColor ? colors.primary : colors.grey}
                    />
                ),
                label: t('targetList.outcome.package'),
                className: wrapLabelClass(
                    'target-list--enrolled',
                    shouldShowColor
                ),
                background: colors.InteractionsGreen
            }
    }
}

const getFallbackLabel = (shouldShowColor: boolean): RecommendedAction => ({
    icon: <SacIcon height="22" width="22" />,
    label: t('targetList.outcome.package'),
    className: wrapLabelClass('target-list--follow-up', shouldShowColor),
    background: colors.InteractionsGreen
})

export const getRecommendedAction = ({
    outcome,
    desiredOutcome,
    duplicate,
    paymentSummary,
    shouldShowColor,
    isVisitedClient
}: GetRecommendedActionProps): RecommendedAction => {
    const percentage = paymentSummary?.percentage ?? 0
    const allEnrollment = paymentSummary?.allEnrollment ?? 0

    if (duplicate) return getDuplicateLabel()
    if (outcome === InteractionOutcomes.NOT_INTERESTED) {
        return getNotInterestedLabel()
    }
    if (percentage >= 100) return getFinisherLabel(shouldShowColor)

    const label = isVisitedClient
        ? getVisitedLabel(outcome, allEnrollment, shouldShowColor)
        : getUnvisitedLabel(
              desiredOutcome,
              allEnrollment,
              percentage,
              shouldShowColor
          )

    return label || getFallbackLabel(shouldShowColor)
}
